import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { 
  Building2, 
  Package, 
  Clock, 
  AlertTriangle,
  ArrowUpRight 
} from "lucide-react";
import { toast } from "sonner";

const ManagementDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, []);

  const loadDashboard = async (signal) => {
    try {
      const response = await axios.get("/dashboard/management", { signal });
      setDashboard(response.data);
    } catch (error) {
      if (!axios.isCancel(error)) toast.error(t('messages.loadFailed'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">{t('engineer.loadingDashboard')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      title: t('dashboard.totalProjects'),
      value: dashboard?.total_projects || 0,
      icon: Building2,
      bg: "bg-blue-50",
      color: "text-blue-600",
      testId: "total-projects-stat"
    },
    {
      title: t('dashboard.totalUnits'),
      value: dashboard?.total_units || 0,
      icon: Package,
      bg: "bg-indigo-50",
      color: "text-indigo-600",
      testId: "total-units-stat"
    },
    {
      title: t('management.expiringHolds'),
      value: dashboard?.expiring_holds || 0,
      icon: Clock,
      bg: "bg-amber-50",
      color: "text-amber-600",
      testId: "expiring-holds-stat"
    },
    {
      title: t('management.unverifiedLogs'),
      value: dashboard?.unverified_logs || 0,
      icon: AlertTriangle,
      bg: "bg-rose-50",
      color: "text-rose-600",
      testId: "unverified-logs-stat"
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div data-testid="management-dashboard" className="space-y-8">
        {/* Header */}
        <div>
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-1">Dashboard</p>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">
            {t('management.title')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('management.subtitle')}</p>
        </div>

        {/* Stats Grid */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Overview
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <button
                  key={stat.title}
                  data-testid={stat.testId}
                  className="group relative bg-white border border-slate-200 rounded-sm p-6 text-left transition-all duration-200 hover:shadow-card-hover hover:border-slate-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-10 h-10 ${stat.bg} rounded-sm flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-600 transition-colors" />
                  </div>
                  <p className="font-mono text-3xl font-semibold text-slate-900 tracking-tight">{stat.value}</p>
                  <p className="text-xs font-medium text-slate-500 mt-1 uppercase tracking-wider">{stat.title}</p>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ManagementDashboard;
